import type { AgeGroupVariationFormData } from "./ageGroupVariationSchema";
import { programPriceStateOf } from "../components/programPriceState";

/**
 * The values the program form opens with: a new program's defaults, or an existing program loaded for
 * edit. Extracted from `useAgeGroupVariationForm` (KB-4281) so the operator CLI
 * (`scripts/camp-data/_operator/shared-program.cjs`) fills a Shared Program exactly as the form does.
 */
export function buildAgeGroupVariationInitialValues(defaultValues?: AgeGroupVariationFormData): AgeGroupVariationFormData {
  // Ensure regularOfferIncrementAlternatives is always an array
  const safeRegularOfferIncrementAlternatives = Array.isArray(defaultValues?.regularOfferIncrementAlternatives)
    ? defaultValues.regularOfferIncrementAlternatives
    : [];

  return {
    key: defaultValues?.key || "",
    visibility: defaultValues?.visibility || "v_published",
    displayTitle: defaultValues?.displayTitle || "",
    description: defaultValues?.description || "",
    eligibilityNotes: defaultValues?.eligibilityNotes || "",
    url: defaultValues?.url || "",
    programUrl: defaultValues?.programUrl || "",
    regUrl: defaultValues?.regUrl || "",
    dataFetchUrl: defaultValues?.dataFetchUrl || "",
    defaultSessionUrl: defaultValues?.defaultSessionUrl || "",
    defaultSessionDescription: defaultValues?.defaultSessionDescription || "",
    // Persist program-level gate for session age overrides
    allowSessionAgeOverride: !!defaultValues?.allowSessionAgeOverride,
    minAgeInYears: defaultValues?.minAgeInYears || null,
    maxAgeInYears: defaultValues?.maxAgeInYears || null,
    minAgeNote: defaultValues?.minAgeNote || "",
    maxAgeNote: defaultValues?.maxAgeNote || "",
    minAgeGradeKey: defaultValues?.minAgeGradeKey || "none",
    maxAgeGradeKey: defaultValues?.maxAgeGradeKey || "none",
    minAgeGradeValue: defaultValues?.minAgeGradeValue || null,
    maxAgeGradeValue: defaultValues?.maxAgeGradeValue || null,
    showMinAgeGradeAsPrimary: defaultValues?.showMinAgeGradeAsPrimary || false,
    showMaxAgeGradeAsPrimary: defaultValues?.showMaxAgeGradeAsPrimary || false,
    offeringTypeKeys: defaultValues?.offeringTypeKeys || [],
    // Only use priceRegular field here for initial values, removing regularPrice
    priceRegular: defaultValues?.priceRegular !== undefined ? defaultValues.priceRegular : 0,
    priceRegularTier2: defaultValues?.priceRegularTier2 ?? null,
    priceRegularTier3: defaultValues?.priceRegularTier3 ?? null,
    priceNote: defaultValues?.priceNote || "",
    pricingTiers: defaultValues?.pricingTiers ?? null,
    // KB-4181 — the program's declared price state; anything but the two states loads as Priced (null).
    priceState: programPriceStateOf(defaultValues?.priceState),
    regularOfferIncrementKey: defaultValues?.regularOfferIncrementKey || "",
    numDays: defaultValues?.numDays || null, // Added numDays field
    timings: {
      regularDropOffStart: defaultValues?.timings?.regularDropOffStart ?? 540, // Default 9:00 AM
      regularDropOffEnd: defaultValues?.timings?.regularDropOffEnd ?? 540, // Default 9:00 AM
      regularProgrammingStart: defaultValues?.timings?.regularProgrammingStart ?? 540, // Default 9:00 AM
      regularProgrammingEnd: defaultValues?.timings?.regularProgrammingEnd ?? 1020, // Default 5:00 PM
      regularPickUpStart: defaultValues?.timings?.regularPickUpStart ?? 1050, // Default 5:30 PM
      regularPickUpEnd: defaultValues?.timings?.regularPickUpEnd ?? 1050, // Default 5:30 PM
      beforeCareStart: defaultValues?.timings?.beforeCareStart || null,
      beforeCareEnd: defaultValues?.timings?.beforeCareEnd || null,
      afterCareStart: defaultValues?.timings?.afterCareStart || null,
      afterCareEnd: defaultValues?.timings?.afterCareEnd || null,
      dropOffStart: defaultValues?.timings?.dropOffStart || null,
      dropOffEnd: defaultValues?.timings?.dropOffEnd || null,
      pickUpStart: defaultValues?.timings?.pickUpStart || null,
      pickUpEnd: defaultValues?.timings?.pickUpEnd || null,
      timingNote: defaultValues?.timings?.timingNote || "",
      // New optional day fields: default to empty string for UI consistency
      dropOffDay: defaultValues?.timings?.dropOffDay ?? "",
      pickUpDay: defaultValues?.timings?.pickUpDay ?? "",
    },
    // Use the safe array instead of potentially undefined value
    regularOfferIncrementAlternatives: safeRegularOfferIncrementAlternatives,
  };
}
